import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV } from "../lib/content";

const Logo = ({ onClick }) => (
  <a href="#home" onClick={onClick} data-testid="header-logo" className="flex items-center gap-2 group">
    <span className="font-serif text-2xl tracking-tight text-ivory">KRV</span>
    <span className="font-serif text-2xl tracking-tight text-gold">Nexus</span>
  </a>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-navy/80 backdrop-blur-xl border-b border-white/10 py-4" : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between">
          <Logo />

          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="link-underline text-sm tracking-wide text-ivory/80 hover:text-ivory transition-colors"
              >
                {n.label}
              </a>
            ))}
          </nav>

          <a
            href="#businesses"
            data-testid="header-cta"
            className="hidden lg:inline-flex items-center gap-2 bg-gold text-navy px-5 py-2.5 rounded-full text-sm font-medium hover:bg-gold-muted transition-colors group"
          >
            Explore Our Businesses
            <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-ivory p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-navy lg:hidden flex flex-col justify-center px-8 noise-overlay"
          >
            <nav className="flex flex-col gap-6">
              {NAV.map((n, i) => (
                <motion.a
                  key={n.href}
                  href={n.href}
                  onClick={close}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="font-serif text-4xl text-ivory/90"
                >
                  {n.label}
                </motion.a>
              ))}
              <a
                href="#businesses"
                onClick={close}
                data-testid="mobile-header-cta"
                className="mt-6 inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 rounded-full text-sm font-medium w-fit"
              >
                Explore Our Businesses <ArrowUpRight size={16} />
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
